import clsx from "clsx";
import React from "react";
import { Loader } from "../../common/components/Loader";
import { SearchActions } from "../actions/SearchActions";
import { BypassBlock } from "../header/BypassBlock";
import { NoSearchResults } from "../NoSearchResults";
import { SearchResultsPreviewContainer } from "../preview/SearchResultsPreviewContainer";
import useSearchState from "../useSearchState";
import { Paging } from "./paging/Paging";
import { Showing } from "./paging/Showing";
import { ResultsHeader } from "./ResultsHeader";
import { SearchResults } from "./SearchResults";
import { downloadType, searchesType, searchTitleType, supportLinkType } from "../../common/types/search/search";
import { resultTableConfigType } from "../../common/types/search/results";
import { SearchContainerRestProps } from "../SearchContainer";

interface SearchResultsContainerProps extends SearchContainerRestProps {
	download: downloadType;
	namespace: string;
	onClearSearch: React.MouseEventHandler<HTMLButtonElement>;
	pagingEnabled: boolean;
	resultTableConfig: resultTableConfigType;
	searches: searchesType;
	searchTitle: searchTitleType;
	supportLink?: supportLinkType;
}

export function SearchResultsContainer({
	additionalAction,
	additionalSearch,
	additionalSearchLink,
	actionsButtonDependsOn,
	contentCredits,
	disclaimers,
	download,
	footnotes,
	footnoteSingle,
	layoutSwitcher,
	namespace,
	onClearSearch,
	pagingEnabled,
	tabSearch,
	resultTableConfig,
	searches,
	searchTitle,
	supportLink,
	views,
}: SearchResultsContainerProps) {
	const {
		loading,
		error,
		paging,
		resultExists,
		resultIsInvalid,
		resultsAreLoaded,
		warnings,
	} = useSearchState([""]);

	let content;
	let pagination;
	let showing;

	if (loading) {
		content = <Loader label="Loading results..." />;
	} else if (error || !resultExists) {
		content = (
			<NoSearchResults
				error={error}
				resultIsInvalid={resultIsInvalid}
				additionalSearchLink={additionalSearchLink}
				warnings={warnings}
			/>
		);
	} else {
		content = (
			<SearchResults
				resultTableConfig={resultTableConfig}
				paging={paging}
				additionalSearchLink={additionalSearchLink}
				disclaimers={disclaimers}
				footnotes={footnotes}
				warnings={warnings}
				footnoteSingle={footnoteSingle}
				contentCredits={contentCredits}
			/>
		);
		pagination = pagingEnabled && <Paging paging={paging} />;
		showing = pagingEnabled && <Showing paging={paging} />;
	}

	const resultsPreview = <SearchResultsPreviewContainer />;

	return (
		<div className="search-results-container">
			<ResultsHeader
				actionsButtonDependsOn={actionsButtonDependsOn}
				supportLink={supportLink}
				hasAdditionalSearch={!!additionalSearch}
				namespace={namespace}
				onClearSearch={onClearSearch}
				pagination={pagination}
				resultsPreview={resultsPreview}
				searchTitle={searchTitle}
			/>

			<div className="search-results" id="searchResults">
				{tabSearch}

				{resultsAreLoaded && (
					<BypassBlock
						skipTo="resultsContainer"
						label="Skip to search results table"
					/>
				)}

				<SearchActions
					namespace={namespace}
					resultsPreview={resultsPreview}
					resultsAreLoaded={resultsAreLoaded}
					download={download}
					searchesEnabled={searches.searchesEnabled}
					views={views}
					layoutSwitcher={layoutSwitcher}
					additionalAction={additionalAction}
					additionalSearch={additionalSearch}
				/>

				<div
					className={clsx("results-container", { loading })}
					id="resultsContainer"
				>
					{content}
				</div>
			</div>

			<footer className="search-footer grid">
				{showing}
				{pagination}
			</footer>
		</div>
	);
}
